import React, { useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { AppConfigFeatures } from '@i18n-ecom-seller/build-runtime/appConfig';
import { APP_ID } from '@i18n-ecom-seller/build-runtime/mainConfig';
import { Loading } from '@/components/Loading';
import loadable from '@loadable/component';
import Create from './containers/create';
import 'tailwindcss/tailwind.css';
import '@oec-seller/sdk/styles/common.scss';
import '@oec-seller/sdk/styles/ui.global.scss';
import M4bConfigProvider from '@m4b-design/config-provider';
import ConfigProvider from '@m4b-design/config-provider-next';
import { LanguageListProvider } from '@oec-seller/sdk/hooks';
import { useTranslation } from 'react-i18next';
import { Slardar } from './lazyApp';
import { SvgDefs, localeEn } from '@m4b-seller/utils';
import { LanguageProvider } from './context';
import { M4BLanguageMap } from './constants';
import { REGION_CONFIG } from './region-config';

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type RequestIdleCallbackOptions = {
  timeout?: number;
};

type RequestIdleCallback = (
  callback: (deadline: IdleDeadlineLike) => void,
  options?: RequestIdleCallbackOptions
) => number;

type RunWhenIdleOptions = {
  timeout?: number;
};

const runWhenIdle = (callback: () => void, options: RunWhenIdleOptions = {}): number => {
  const requestIdleCallback = (window as unknown as { requestIdleCallback?: RequestIdleCallback }).requestIdleCallback;
  const timeout = options.timeout ?? 1500;

  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(
      () => {
        callback();
      },
      {
        timeout
      }
    );
  }

  return window.setTimeout(() => {
    callback();
  }, timeout);
};


type AppProps = {
  m4bContext?: Record<string, unknown>;
  m4bArcoContext?: ({ locale?: any; prefixCls?: string } & Record<string, unknown>) | undefined;
};

const LogisticsModificationCreateWithoutUSPS = loadable(
  () => import(/* webpackPrefetch: true */ './containers/logistics-modification-create-without-USPS'),
  { fallback: <Loading /> }
);

function App(props: AppProps & { _i18n?: { loading?: boolean } }): JSX.Element {
  const { m4bContext = {}, m4bArcoContext = {} } = props;
  const { i18n } = useTranslation();

  const [m4bLanguage, setM4bLanguage] = useState(localeEn);
  const defaultLanguageList = REGION_CONFIG.language.switchLang;
  const [languageSupportList, setLanguageSupportList] = useState<string[]>(defaultLanguageList);
  const originURL = window?.location?.origin;
  const regionMap = {
    ttp: 'seller-us',
    gcp: 'seller.eu'
  };
  let region = '';
  for (const key in regionMap) {
    if (originURL?.includes(regionMap[key])) {
      region = key;
    }
  }

  useEffect(() => {
    const teaInitConfig: any = {
      ...window.__SELLER_APP_ENVS__.tea,
      appId: '298722',
      ['app_id']: '298722'
    };

    runWhenIdle(() => {
      import('@ies/csp_event_tracking')
        .then(({ initTea }) => {
          initTea(teaInitConfig);
        })
        .catch(() => {
          // ignore
        });

      import('./components/slider/slider')
        .then(({ sliderView }) => {
          sliderView.init({
            aid: APP_ID,
            isOversea: false,
            region
          });
        })
        .catch(() => {
          // ignore
        });

      Slardar.start();
    });
  }, [region]);

  useEffect(() => {
    const locale = i18n.language;

    runWhenIdle(() => {
      import('@tiktok-fe/web-cookie-banner')
        .then(m => {
          m?.default?.loadCookieBanner?.({
            locale
          });
        })
        .catch(() => {
          // ignore
        });
    });
  }, [i18n.language]);

  useEffect(() => {
    const nextM4bLanguage = M4BLanguageMap[i18n.language] || localeEn;
    setM4bLanguage(nextM4bLanguage);
  }, [i18n.language]);

  const { prefixCls: _prefixCls, ...rest } = m4bContext;
  const { prefixCls: _arcoPrefix, ...arcoConfigRest } = m4bArcoContext;

  return (
    // @ts-ignore: third-party typings
    <ConfigProvider language={m4bArcoContext.locale} {...rest} arcoConfig={{ ...arcoConfigRest }}>
      {/* @ts-ignore: third-party typings */}
      <M4bConfigProvider
        {...m4bContext}
        arcoConfig={{
          ...m4bArcoContext,
          prefixCls: 'theme-arco'
        }}
        // @ts-ignore: M4bConfigProvider supports passing locale through value
        value={{ locale: m4bLanguage }}
        prefixCls="theme-m4b"
      >
        <SvgDefs />

        <LanguageProvider>
          {/* @ts-ignore: third-party typings */}
          <LanguageListProvider
            languageList={defaultLanguageList}
            languageSupportList={languageSupportList}
            setLanguageSupportList={setLanguageSupportList}
          >
            <div className="h-full">
              <Switch>
                <Route exact={true} path="/contact-us" component={Create} />
                <Route
                  exact={true}
                  path="/contact-us/logistics-modification"
                  component={LogisticsModificationCreateWithoutUSPS}
                />
                <Route
                  exact={true}
                  path="/contact-us/logistics-modification-new"
                  component={LogisticsModificationCreateWithoutUSPS}
                />
              </Switch>
            </div>
          </LanguageListProvider>
        </LanguageProvider>
      </M4bConfigProvider>
    </ConfigProvider>
  );
}

App.config = {
  features: AppConfigFeatures
};

export default App;
